import { Checkbox, Col, Form, Row, Select } from 'antd';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import patterns from '../../config/patterns';
import { countriesWithStates } from '../../constants/country-states';
import { AppRoutes } from '../../enums';
import { CompanyScale } from '../../types/company-scale';
import { getConfig } from '../../utils/config';
import { taApiClient } from '../../utils/http-client';
import { camelCaseToWord } from '../../utils/strings';
import { TASelect } from '../dashboard/styles';
import { PhoneInput } from '../phone-input/phone-input';
import { ButtonType, TAButton, TAInput, TAPasswordInput } from '../widgets';
import './antd.form.css';

let { phoneNumberPattern } = patterns;
const config = getConfig();

let countries = Object.keys(countriesWithStates);

export const SignUpForm: React.FC<any> = ({ callback, style }) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [errors, setErrors] = useState<any>();
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const [country, setCountry] = useState<string>();

	const onFinish = async values => {
		if (values.contact.phoneNumber?.phone)
			values.contact.phoneNumber = `${values.contact.phoneNumber.code}-${values.contact.phoneNumber.phone}`;

		if (values.company.phone?.phone)
			values.company.phone = `${values.company.phone.code}-${values.company.phone.phone}`;

		let response;
		try {
			response = await taApiClient.post(config.URIs.sign_up_uri, values);
			let responseOK = response && Math.round(response.status / 100) === 2;
			if (!responseOK) throw new Error('Network Error, Please contact support for help.');
		} catch (e: any) {
			setErrors({ ...errors, ...(e.errors || {}) });
			return;
		} finally {
			setLoading(false);
			callback && callback(response.data.token, response.data.profile);
			navigate(AppRoutes.ACCOUNT_CONFIRMATION);
		}
	};

	const checkIfEqualFirstPassword = (rule, value) => {
		if (value && value !== form.getFieldValue(['account', 'password']))
			return Promise.reject('Two passwords that you entered are inconsistent!');
		return Promise.resolve();
	};

	const checkAgreement = (rule, value, callback) => {
		if (!value) callback('you must agree to our terms to proceed.');
		else callback();
	};

	return (
		<Form layout="vertical" form={form} onFinish={onFinish} className="ta-form" style={style}>
			<fieldset className="ta-form-section" id="account-section">
				<legend className="ta-form-section-title">Account information</legend>

				<Form.Item
					label="Email"
					validateStatus={errors?.account?.email ? 'error' : undefined}
					help={errors?.account?.email || undefined}
					name={['account', 'email']}
					rules={[
						{ required: true, message: 'Please input your email.' },
						{ type: 'email', message: 'Invalid email address.' },
						{
							validator: (rule, value, callback) => {
								if (patterns.businessEmailPattern.test(value.split('@')[1])) return callback();
								return callback('You must use your business email.');
							}
						}
					]}
				>
					<TAInput
						onChange={val => {
							if (errors?.account?.email) {
								setErrors({
									...errors,
									account: {
										...errors.account,
										email: null
									}
								});
							}
						}}
					/>
				</Form.Item>
				<Form.Item
					label="Password"
					name={['account', 'password']}
					rules={[
						{ required: true, message: 'Please enter password.' },
						{
							pattern: patterns.passwordPattern,
							message: (
								<p style={{ marginBottom: '1px' }}>
									Password must contain at least 8 characters including at least one alphabet and one
									number and no spaces
								</p>
							)
						}
					]}
				>
					<TAPasswordInput />
				</Form.Item>

				<Form.Item
					label="Confirm Password"
					name={['account', 'confirmPassword']}
					rules={[
						{
							required: true,
							message: 'Please enter password confirmation.'
						},
						{ validator: checkIfEqualFirstPassword }
					]}
				>
					<TAPasswordInput />
				</Form.Item>
			</fieldset>

			<fieldset className="ta-form-section" id="company-section">
				<legend className="ta-form-section-title">Company information</legend>

				<Form.Item
					label="Company Name"
					validateStatus={errors?.company?.name ? 'error' : undefined}
					help={errors?.company?.name || undefined}
					name={['company', 'name']}
					rules={[{ required: true, message: 'Please input the company Name.' }]}
				>
					<TAInput
						onChange={val => {
							if (errors?.company?.name) {
								setErrors({
									...errors,
									company: {
										...errors?.company,
										name: null
									}
								});
							}
						}}
					/>
				</Form.Item>

				<Form.Item
					label="Company Website"
					name={['company', 'onlinePresence', 'website']}
					rules={[
						{
							required: true,
							message: 'Enter the Company website’s link'
						},
						{ pattern: patterns.urlPattern, message: 'Not valid url' }
					]}
				>
					<TAInput />
				</Form.Item>

				<Form.Item
					label="Company Phone"
					name={['company', 'phone']}
					rules={[
						{ required: true },
						{
							validator: (rule, value, callback) => {
								if (!value || !value.code || !value.phone) {
									callback('Phone number should be added');
								} else if (value.phone && !phoneNumberPattern.test(value.phone)) {
									callback('Not valid phone number');
								} else callback();
							}
						}
					]}
				>
					<PhoneInput />
				</Form.Item>

				<Form.Item
					label="Company Scale"
					name={['company', 'scale']}
					rules={[
						{
							required: true,
							message: 'Please enter the company scale'
						}
					]}
				>
					<TASelect>
						{Object.values(CompanyScale).map(s => (
							<Select.Option value={s} key={s}>
								{camelCaseToWord(s)}
							</Select.Option>
						))}
					</TASelect>
				</Form.Item>

				<Form.Item
					label="Country"
					name={['company', 'location', 'country']}
					rules={[{ required: true, message: 'Please add company country.' }]}
				>
					<TASelect
						onChange={e => {
							const countryEvent = e as string;
							if (e) setCountry(countryEvent);
						}}
						placeholder="Country"
						showSearch
						filterOption={(input, option) =>
							option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
						}
					>
						{countries.map(country => (
							<Select.Option value={country} key={country}>
								{country}
							</Select.Option>
						))}
					</TASelect>
				</Form.Item>

				<Form.Item
					label="City"
					name={['company', 'location', 'city']}
					rules={[{ required: true, message: 'Please add company city.' }]}
				>
					{/* <TASelect
						showSearch
						filterOption={(input, option) =>
							option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
						}
					> */}
					<TASelect disabled={!country} placeholder="City" showSearch>
						{country
							? countriesWithStates[country].map(state => (
									<Select.Option value={state} key={state}>
										{state}
									</Select.Option>
							  ))
							: null}
					</TASelect>
				</Form.Item>
				<Form.Item
					label="Area"
					name={['company', 'location', 'area']}
					rules={[{ required: true, message: 'Please add company area.' }]}
				>
					<TAInput placeholder="Please make it detailed or add maps location" />
				</Form.Item>

				<Form.Item label="Referred by" name={['company', 'referral']}>
					<TAInput />
				</Form.Item>
			</fieldset>

			<fieldset className="ta-form-section" id="contact-section">
				<legend className="ta-form-section-title">Contact information</legend>
				<Row gutter={8}>
					<Col xs={12} md={24}>
						<Form.Item
							label="First Name"
							name={['contact', 'firstName']}
							rules={[
								{
									required: true,
									message: 'Please input your first name.'
								},
								{ pattern: patterns.spacedNames, message: 'Not valid name' }
							]}
						>
							<TAInput />
						</Form.Item>
					</Col>
					<Col xs={12} md={24}>
						<Form.Item
							label="Last Name"
							name={['contact', 'lastName']}
							rules={[
								{ required: true, message: 'Please enter last name.' },
								{ pattern: patterns.spacedNames, message: 'Not valid name' }
							]}
						>
							<TAInput />
						</Form.Item>
					</Col>
				</Row>

				<Form.Item
					label="Title"
					name={['contact', 'title']}
					rules={[{ required: true, message: 'Please enter your title.' }]}
				>
					<TAInput />
				</Form.Item>
				<Form.Item
					label="Phone"
					name={['contact', 'phoneNumber']}
					rules={[
						{ required: true },
						{
							validator: (rule, value, callback) => {
								if (!value || !value.code || !value.phone) {
									callback('Phone number should be added');
								} else if (value.phone && !phoneNumberPattern.test(value.phone)) {
									callback('Not valid phone number');
								} else callback();
							}
						}
					]}
				>
					<PhoneInput />
				</Form.Item>
			</fieldset>
			<Form.Item name="agreement" rules={[{ validator: checkAgreement }]} valuePropName="checked">
				<Checkbox className="ta-checkbox w-100">
					I agree on talents arena{' '}
					<Link to="/terms-of-use" target="_blank">
						terms of use
					</Link>
					.
				</Checkbox>
			</Form.Item>
			<Form.Item>
				<TAButton
					htmlType="submit"
					btntype={ButtonType.PRIMARY}
					style={{
						width: '100%',
						padding: '.8rem 0.8rem',
						borderRadius: '.9rem',
						fontSize: '.9rem',
						fontWeight: 500,
						lineHeight: '1.36',
						height: 'auto'
					}}
					loading={loading}
				>
					Sign Up
				</TAButton>
			</Form.Item>

			<Link to={AppRoutes.SIGNIN}>Already have an account?</Link>
		</Form>
	);
};
